import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, MailCheck, AlertCircle, Home, Lock, Send } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/store/auth';
import { api } from '@/lib/api';
import { googleAvailable } from '@/lib/google';
import { clearToken, loadTokenBrand, tokenPending, useBrand } from '@/lib/brand';
import { afterLoginPath, safeNext } from '@/lib/brandSources';
import { BrandBand } from '@/components/BrandBand';

/**
 * Where the building's band will be, while its brand is still on the way: a
 * neutral block of the same size, never TendedLoop's logo that would then swap.
 */
function BandSkeleton() {
  return (
    <div className="mb-6" aria-hidden data-testid="band-skeleton">
      <div className="flex min-h-[240px] animate-pulse items-center justify-center bg-slate-100 px-6 pb-10 pt-12">
        <div className="h-16 w-[200px] max-w-full rounded-xl bg-slate-200" />
      </div>
    </div>
  );
}

const GOOGLE_ERRORS: Record<string, string> = {
  RESIDENT_NOT_INVITED: 'חשבון ה-Google הזה לא רשום אצל צוות הדיור. היכנסו עם קישור למייל שלכם, או בקשו מהצוות הזמנה.',
  RESIDENT_USE_EMAIL_LINK: 'הכתובת הזו רשומה ביותר ממקום מגורים אחד. היכנסו עם קישור למייל.',
};

/** "Continue with Google" for a resident the housing team already invited. */
function GoogleSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const googleSignIn = useAuth((s) => s.googleSignIn);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (!googleAvailable()) return null;
  return (
    <div className="mt-5">
      <div className={busy ? 'pointer-events-none opacity-60' : ''}>
        <div className="flex justify-center">
          <GoogleLogin
            onSuccess={async (res) => {
              if (!res.credential) return setError('הכניסה עם Google לא הושלמה. נסו שוב.');
              setBusy(true);
              setError('');
              const ok = await googleSignIn(res.credential);
              setBusy(false);
              if (ok) return onSignedIn();
              const code = useAuth.getState().errorCode;
              setError(
                (code && GOOGLE_ERRORS[code]) || 'הכניסה עם Google לא הושלמה. נסו שוב או היכנסו עם קישור למייל.',
              );
            }}
            onError={() => setError('הכניסה עם Google לא הושלמה. נסו שוב או היכנסו עם קישור למייל.')}
            text="continue_with"
            shape="pill"
            width={300}
          />
        </div>
      </div>
      {error && (
        <p className="mt-3 text-start text-[15px] leading-relaxed text-red-600" role="alert">
          {error}
        </p>
      )}
      <div className="my-5 flex items-center gap-3 text-[15px] text-slate-400" aria-hidden>
        <span className="h-px flex-1 bg-slate-200" />
        או
        <span className="h-px flex-1 bg-slate-200" />
      </div>
    </div>
  );
}

/** Ask for a fresh sign-in link by email. Same answer whether or not the email is on file. */
function RequestLinkForm({ next }: { next: string }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setState('error');
      setError('נא להזין כתובת אימייל תקינה');
      return;
    }
    setState('sending');
    try {
      await api.requestLink(email.trim(), next);
      setState('sent');
    } catch (err) {
      setState('error');
      setError((err as Error).message);
    }
  }

  if (state === 'sent') {
    return (
      <p
        className="mt-4 rounded-xl bg-brand-50 p-4 text-start text-[15px] leading-relaxed text-brand-900"
        role="status"
      >
        אם הכתובת רשומה אצל צוות הדיור, קישור כניסה חדש בדרך אליה. בדקו את תיבת הדואר (וגם את הספאם).
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="mt-5 text-start" noValidate>
      <label htmlFor="request-link-email" className="mb-1.5 block text-[15px] font-semibold text-slate-700">
        קבלת קישור כניסה חדש
      </label>
      <input
        id="request-link-email"
        type="email"
        inputMode="email"
        autoComplete="email"
        dir="ltr"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="name@example.com"
        className="min-h-[48px] w-full rounded-xl border border-slate-200 px-3 text-[16px] outline-none focus:border-brand-500"
      />
      {state === 'error' && <p className="mt-1.5 text-[15px] text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={state === 'sending'}
        className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 text-[16px] font-semibold text-white disabled:opacity-60"
      >
        {state === 'sending' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        שליחת קישור למייל
      </button>
    </form>
  );
}

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const expired = params.get('expired') === '1';
  // Only an internal path (e.g. "/lease"), and the org hint goes along.
  const hint = useBrand((s) => s.hintHandle);
  const nextPath = safeNext(params.get('next'));
  const next = afterLoginPath(nextPath, hint);
  const { verifyLink, loading, error, isAuthenticated } = useAuth();
  const started = useRef(false);

  // With a link in the URL, move on only once THAT link has verified: a session
  // already on this device must not carry the resident past a different link.
  // Its office's brand decides this page; asked for only when the link did not
  // sign in (it then still names its office, valid or expired).
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    tokenPending();
    void verifyLink(token).then((ok) => {
      if (ok) navigate(next, { replace: true });
      else void loadTokenBrand(token);
    });
  }, [token, verifyLink, navigate, next]);
  useEffect(() => () => clearToken(), []);

  useEffect(() => {
    if (!token && isAuthenticated) navigate(next, { replace: true });
  }, [token, isAuthenticated, navigate, next]);

  const verifying = !!token && (loading || (!error && !isAuthenticated));
  // The link's office, else a returning resident's building (kept on this
  // device after a sign-in), else the link's org hint.
  const brand = useBrand((s) => s.brand);
  const brandLoading = useBrand((s) => s.loading);
  const banded = !!brand || brandLoading;

  return (
    <div className="min-h-screen">
      {brand ? <BrandBand brand={brand} size="hero" /> : brandLoading && <BandSkeleton />}
      <div
        className={
          'mx-auto flex max-w-md flex-col items-center px-5 text-center ' +
          (banded ? 'pb-8' : 'min-h-screen justify-center')
        }
      >
        {!banded && (
          <>
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-lg">
              <Home className="h-8 w-8" />
            </div>
            <p className="mb-6 text-lg font-bold tracking-tight text-brand-900" dir="ltr">
              TendedLoop
            </p>
          </>
        )}

        {verifying && (
          <>
            <Loader2 className="mb-3 h-7 w-7 animate-spin text-brand-500" />
            <p className="text-lg text-brand-900">מתחברים…</p>
          </>
        )}

        {token && !loading && error && (
          <div className="w-full rounded-2xl border border-red-100 bg-white p-6 shadow-sm">
            <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
            <h1 className="mb-1 text-xl font-bold text-brand-900">הקישור אינו תקף</h1>
            <p className="text-[15px] leading-relaxed text-slate-600">
              ייתכן שהקישור פג או שנשלח במקומו קישור חדש יותר. אפשר לקבל קישור חדש למייל:
            </p>
            <GoogleSignIn onSignedIn={() => navigate(next, { replace: true })} />
            <RequestLinkForm next={nextPath} />
          </div>
        )}

        {!token && (
          <div className="w-full rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <MailCheck className="mx-auto mb-3 h-9 w-9 text-brand-500" />
            <h1 className="mb-2 text-xl font-bold text-brand-900">
              {expired ? 'החיבור הסתיים' : 'כניסה לפורטל הדיירים'}
            </h1>
            <p className="text-[15px] leading-relaxed text-slate-600">
              {expired
                ? googleAvailable()
                  ? 'מטעמי אבטחה יש להתחבר מחדש: עם Google, או עם קישור כניסה למייל.'
                  : 'מטעמי אבטחה יש להתחבר מחדש. הזינו את האימייל ונשלח קישור כניסה.'
                : googleAvailable()
                  ? 'נכנסים בלי סיסמה: עם חשבון Google של האימייל שמסרתם לצוות הדיור, או עם קישור כניסה למייל.'
                  : 'הכניסה מתבצעת דרך קישור אישי שנשלח במייל מצוות הדיור, ללא סיסמה. אין לכם קישור בתוקף? הזינו את האימייל:'}
            </p>
            <GoogleSignIn onSignedIn={() => navigate(next, { replace: true })} />
            <RequestLinkForm next={nextPath} />
          </div>
        )}

        {!verifying && (
          <p className="mt-6 flex items-center gap-1.5 text-[15px] text-slate-500">
            <Lock className="h-4 w-4" /> כניסה מאובטחת · הפרטים נשמרים מוצפנים
          </p>
        )}
        {/* The app is TendedLoop's; the band above is the building's. */}
        {brand && (
          <p className="mt-4 text-[15px] text-slate-500">
            <b className="font-bold text-slate-800" dir="ltr">
              TendedLoop
            </b>{' '}
            · פורטל הדיירים
          </p>
        )}
      </div>
    </div>
  );
}
